import React, {useRef, useState} from 'react'
import {StyleProps} from '../../'
import Button from '../button/Button'
import styled from 'styled-components'
import Plus from '../icons/Plus'
import Box from '../layout/Box'
import Text from '../text/Text'

interface AccordionButtonProps extends React.HTMLAttributes<HTMLAnchorElement | HTMLButtonElement> {
  className?: string
  id: string
  headline: React.ReactNode
  content: React.ReactNode
  $styles?: StyleProps
}

const AccordionItem = styled(Box).attrs({
  as: 'li',
  $mt: 8,
  $styles: {
    border: '1px solid transparent',
    borderRadius: 16,
    background: 'rgba(5, 0, 56, 0.05)',
  },
})<{$isFocused?: boolean}>`
  ${({$isFocused, theme}) => $isFocused && theme.normalizeStyles(theme.focus.outlines.default)}
`

const IconWrapper = styled.div<{
  $isActive?: boolean
}>`
  opacity: 0.4;
  transition: 250ms ease;
  transition-property: opacity, transform;

  ${AccordionItem}:hover &,
  &.is-expanded {
    opacity: 1;
  }

  &.is-expanded {
    transform: rotate(45deg);
  }
`

const HeaderButton = styled(Button)`
  display: block;
  width: 100%;
  text-align: left;
`

const AccordionItemHeader = styled(Box).attrs<{$isExpanded: boolean}>(({$isExpanded}) => ({
  $styles: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: [20, 40],
    paddingBottom: $isExpanded ? [16, 28] : [24, 44],
    paddingLeft: [24, 40],
    paddingRight: [24, 40],
  },
}))<{$isExpanded: boolean}>``

const AccordionItemContent = styled(Text).attrs({
  as: 'section',
  $styles: {
    color: 'rgba(5, 0, 56, 0.6)',
    maxWidth: [null, 645],
    paddingBottom: [24, 32],
    paddingLeft: [24, 40],
    paddingRight: [24, 40],
  },
})``

const AccordionButtonBase: React.FC<AccordionButtonProps> = ({id, headline, content}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isFocused, setFocused] = useState(false)

  const contentId = `accordion-content-${id}`
  const accordionHeaderId = `accordion-header-${id}`

  const buttonRef = useRef<HTMLButtonElement>(null)

  return (
    <AccordionItem $isFocused={isFocused}>
      <HeaderButton
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        onFocus={e => {
          if (e.target === buttonRef.current) {
            setFocused(true)
          }
        }}
        onBlur={e => {
          setFocused(false)
        }}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        id={accordionHeaderId}
      >
        <AccordionItemHeader $isExpanded={isExpanded}>
          <Box $styles={{maxWidth: [225, 740]}}>{headline}</Box>
          <IconWrapper className={isExpanded ? 'is-expanded' : undefined} aria-hidden={true}>
            <Plus />
          </IconWrapper>
        </AccordionItemHeader>
      </HeaderButton>
      {isExpanded && (
        <AccordionItemContent
          id={contentId}
          aria-labelledby={accordionHeaderId}
          $variant={['p1', 'p0']}
        >
          {content}
        </AccordionItemContent>
      )}
    </AccordionItem>
  )
}

export default AccordionButtonBase
